import React from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Icon, Label, Row } from '@folio/stripes-components';
import { uniqueId } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from '../styles.css';
import HelpeModal from '../../HelpeModal';

const propTypes = {
  fields: PropTypes.object,
  meta: PropTypes.object,
  fieldValues: PropTypes.object,
  RenderFieldComponent: PropTypes.elementType,
  showHelpeIcon: PropTypes.bool,
  controlledVocab: PropTypes.object,
  actionType: PropTypes.string,
};

function FormFieldArray({
  fields,
  meta,
  fieldValues,
  RenderFieldComponent,
  showHelpeIcon,
  controlledVocab,
  actionType,
}) {
  const { locale } = useIntl();
  const {
    label,
    singularLabel,
    type,
    isRequired,
    isRepeatable,
    fieldArray,
    name,
    defaultValue,
  } = fieldValues;

  const fieldLabel = label.find((field) => field.locale === locale)?.value;
  const unitLabel = singularLabel?.find(
    (field) => field.locale === locale,
  )?.value;

  const onAddNewField = () => {
    if (type === 'fieldArray') {
      const fieldObj = {};
      fieldArray.forEach((key) => {
        if (key.type === 'checkbox') {
          fieldObj[key.name] = JSON.parse(key?.defaultValue);
        } else {
          fieldObj[key.name] = key.defaultValue || null;
        }
      });
      fields.push(fieldObj);
    } else {
      fields.push(defaultValue);
    }
  };

  const renderFieldArray = () => {
    return (
      <>
        <Row>
          <Col xs={showHelpeIcon ? 11 : 12}>
            <Label required={isRequired}>{fieldLabel}</Label>
          </Col>
          {showHelpeIcon && (
            <Col xs={1} className={styles.helpeIcon}>
              <HelpeModal
                fieldData={fieldValues}
                title={fieldLabel}
                iconSize="medium"
              />
            </Col>
          )}
        </Row>
        {fields.map((fieldName, id) => {
          const itemValue = fields.value?.[id];
          const stableKey = itemValue?.authorityId || itemValue?.id || `${fieldValues.name}-${id}-${fields.length}`;
          return (
          <Row key={stableKey}>
            <Col xs={11}>
              <RenderFieldComponent
                index={id}
                fieldValues={fieldValues}
                fieldName={fieldName}
                fields={fields}
                controlledVocab={controlledVocab}
                actionType={actionType}
              />
            </Col>
            {isRepeatable && (
              <Col
                xs={1}
                className={
                  (type === 'fieldArray' && id === 0) ||
                  name === 'succeedingTitles' ||
                  name === 'precedingTitles'
                    ? styles.margin_btn
                    : null
                }
              >
                <Button
                  id={`inventory-templates-localized-field-remove-${fieldValues.name}-${id}-btn`}
                  buttonStyle="slim"
                  onClick={() => fields.remove(id)}
                >
                  <Icon icon="trash" />
                </Button>
              </Col>
            )}
          </Row>
          );
        })}
        {isRepeatable && (
          <Button
            id={uniqueId('inventory-templates-localized-field-add-btn')}
            onClick={() => onAddNewField()}
            icon="plus-sign"
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.formGenerator.addBtn"
              values={{ unitLabel }}
              defaultMessage="Add {unitLabel}"
            />
          </Button>
        )}
        <div>
          {meta?.error && meta.touched && !fields?.value?.length && (
            <span style={{ color: 'darkred' }}>{meta?.error}</span>
          )}
        </div>
      </>
    );
  };

  return <div className={styles.container}>{renderFieldArray()}</div>;
}

FormFieldArray.propTypes = propTypes;

export default FormFieldArray;
